import React from 'react'

import { formatString } from '../../../../utilities/helpers'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { Spinner } from '../../../LoadingContainer/Spinner'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

/** Matches `ORGANIZATION_PERMISSION_CATALOG` in tickets (team UI order). */
const PERMISSION_GROUP_KEY_ORDER = [
  'settings',
  'events_packages',
  'orders',
  'tracking_links',
  'reports',
  'marketing',
]

/** Within-group order (same sequence as CMS catalog); values from shared-utils. */
export const PERMISSION_SLUG_DISPLAY_ORDER = [
  ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS,
  ORGANIZATION_PERMISSION_SLUGS.TAX_STATUS,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_TERMINALS,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_SCANNERS,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_CUSTOM_FEES,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_BUYER_TYPES,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_EVENTS,
  ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_FLEX_PACKAGES,
  ORGANIZATION_PERMISSION_SLUGS.FLEX_PACKAGES_MANAGEMENT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_SEASON_PACKAGES,
  ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT,
  ORGANIZATION_PERMISSION_SLUGS.GUEST_LIST,
  ORGANIZATION_PERMISSION_SLUGS.ATTENDEES,
  ORGANIZATION_PERMISSION_SLUGS.CONTACT_ATTENDEES,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDERS,
  ORGANIZATION_PERMISSION_SLUGS.ORDERS_MANAGEMENT,
  ORGANIZATION_PERMISSION_SLUGS.TICKET_SALES,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_TRACKING_LINKS,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_ACCESS_CONTROL_SUMMARY,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_DETAILS_REPORT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_SUMMARY_REPORT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_CUSTOM_FEE_REPORT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_SETTLEMENT_REPORT,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_TICKET_COUNTS,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_EVENT_SALES_AND_ATTENDANCE,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDER_LISTINGS,
  ORGANIZATION_PERMISSION_SLUGS.VIEW_TIME_STAMP_REPORT,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_PROMO_CODES,
  ORGANIZATION_PERMISSION_SLUGS.MANAGE_FUNDRAISING,
]

const MODAL_VISIBLE_PERMISSION_SLUGS = new Set(PERMISSION_SLUG_DISPLAY_ORDER)

function filterToModalVisible(list) {
  if (!Array.isArray(list) || list.length === 0) return []
  return list.filter(
    (p) => p?.slug && MODAL_VISIBLE_PERMISSION_SLUGS.has(p.slug)
  )
}

function sortPermissionsByCatalogOrder(list) {
  if (!Array.isArray(list) || list.length === 0) return []
  const rank = new Map(
    PERMISSION_SLUG_DISPLAY_ORDER.map((permissionSlug, index) => [
      permissionSlug,
      index,
    ])
  )
  const fallback = PERMISSION_SLUG_DISPLAY_ORDER.length
  return [...list].sort((a, b) => {
    const ra = rank.has(a.slug) ? rank.get(a.slug) : fallback
    const rb = rank.has(b.slug) ? rank.get(b.slug) : fallback
    if (ra !== rb) return ra - rb
    return String(a.slug).localeCompare(String(b.slug))
  })
}

function buildOrderedPermissionGroups(permissions) {
  const keys = PERMISSION_GROUP_KEY_ORDER.filter((k) => {
    const visible = filterToModalVisible(permissions[k])
    return visible.length > 0
  })
  return keys.map((groupKey) => ({
    groupKey,
    list: sortPermissionsByCatalogOrder(
      filterToModalVisible(permissions[groupKey])
    ),
  }))
}

/** Turn a group key (e.g. `event_ops`) into a short heading. */
function formatPermissionGroupHeading(key) {
  return key === 'events_packages'
    ? formatString(key, '&', ' & ')
    : formatString(key)
}

export default function CreateRoleModal({
  show,
  handleClose,
  permissions,
  allPermissionsFlat,
  id,
  role,
  setRole,
  handleValid,
  isValid,
  isCheck,
  checker,
  handleSelectAll,
  handleCheck,
  handleCreate,
  isCreating,
  isDisabled,
}) {
  if (!permissions || typeof permissions !== 'object') return <div />

  const flatForChecker = filterToModalVisible(
    Array.isArray(allPermissionsFlat) ? allPermissionsFlat : []
  )

  const permissionGroups = buildOrderedPermissionGroups(permissions)

  return (
    <Modal
      className="modal-sm"
      id="create-role"
      scrollable
      centered
      animation={false}
      backdrop="static"
      fullscreen="md-down"
      show={show}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h4">{id ? 'Edit' : 'New'} role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleCreate}>
          <Form.Group className="mb-4" controlId="name">
            <Form.Label>Role name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Enter name"
              required
              value={role?.name || ''}
              onBlur={handleValid}
              onChange={(e) =>
                setRole({ ...role, [e.target.name]: e.target.value })
              }
              className={!isValid ? 'error-border' : ''}
              autoFocus
              data-testid="create-role-name-input"
            />
            {!isValid && (
              <Form.Text className="error" data-testid="create-role-name-error">
                Role name cannot be {role.name}.
              </Form.Text>
            )}
          </Form.Group>
          <div className="mb-2">
            <h2 className="fs-md">Permissions</h2>
            <p className="subtitle mb-4">
              Grant your team members specific permissions to delegate tasks
            </p>
            <Form.Check
              label="Select all"
              type="checkbox"
              checked={checker(flatForChecker)}
              id="all"
              onChange={handleSelectAll}
            />
          </div>
          <ul>
            {permissionGroups.map(({ groupKey, list }) => (
              <li className="list" key={groupKey}>
                <p className="heading">
                  {formatPermissionGroupHeading(groupKey)}
                </p>
                <ul>
                  {list?.length > 1 && (
                    <li>
                      <Form.Check
                        label="Select all"
                        type="checkbox"
                        checked={checker(list)}
                        id={`group-all-${groupKey}`}
                        name={groupKey}
                        onChange={handleSelectAll}
                      />
                    </li>
                  )}
                  {list.map(
                    ({ slug: permissionSlug, name: permissionName }) => (
                      <li key={permissionSlug}>
                        <Form.Check
                          type="checkbox"
                          label={permissionName}
                          id={permissionSlug}
                          onChange={handleCheck}
                          checked={isCheck.includes(permissionSlug)}
                        />
                      </li>
                    )
                  )}
                </ul>
              </li>
            ))}
          </ul>
        </Form>
      </Modal.Body>
      <Stack className="btn-group-flex">
        <Button
          size="lg"
          disabled={isDisabled || isCheck?.length === 0}
          onClick={handleCreate}
        >
          {isCreating ? (
            <Spinner />
          ) : (
            `${id ? 'Update' : 'Save and create'} role`
          )}
        </Button>
      </Stack>
    </Modal>
  )
}
